import React, { useMemo, useRef, useState } from 'react';
import { MdClose, MdFilterList } from 'react-icons/md';
import {
  ChipMenu,
  EmptyState,
  EMPTY_STATE_SIZE_SM,
  IconButton,
  SearchInput,
} from '@aibuild/ui';
import { useNodeDefinitions } from '../../hooks/useNodeDefinitions';
import type { NodeDefinitionDto } from '@api/nodeDefinitions';
import NodeLibraryItem from './NodeLibraryItem';
import { resolveIcon } from './iconRegistry';
import {
  CategoryAnchor,
  CategoryChipLabel,
  CategoryChipTrigger,
  CategoryRow,
  CategoryMenuPosition,
  FlyoutHeader,
  FlyoutTitle,
  ItemList,
  Section,
  SectionDot,
  SectionHead,
  SectionList,
  SectionRule,
  SectionTitle,
  SearchRow,
  SidebarRoot,
} from './NodeLibrary.styled';
import type { PaletteType } from '../paletteDrop';

const ALL_CATEGORY = 'All';

interface NodeLibraryProps {
  /** Called when the user closes the flyout (X button click). The
   *  containing flyout owns the visibility / open state — the library
   *  itself just emits the request. */
  onClose: () => void;
  /** Optional callback for click-to-add (drag is handled by NodeLibraryItem). */
  onSelectPaletteType?: (paletteType: PaletteType) => void;
  dataTestId?: string;
}

/**
 * The catalog content for the editor's NodeLibrary flyout. Sources every
 * entry from the live {@code /api/v1/processes/node-definitions} catalog
 * via {@link useNodeDefinitions}; the only frontend-side hardcoding is
 * the icon string-id -> React component mapping in {@link resolveIcon}.
 *
 * Sections come from {@code displayMetadata.group} on each definition;
 * groups are emitted in stable order based on first appearance. Search
 * filters by label / description / tags case-insensitively, and a
 * category chip narrows the visible sections to one group at a time.
 *
 * The flyout chrome (slide-in animation, anchoring against the rail) is
 * owned by {@link NodeLibraryFlyout}; this component renders the
 * panel-internal contents only.
 */
const NodeLibrary: React.FC<NodeLibraryProps> = ({
  onClose,
  onSelectPaletteType,
  dataTestId = 'node-library',
}) => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(ALL_CATEGORY);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const definitionsQuery = useNodeDefinitions();
  const definitionsData = definitionsQuery.data;
  const categoryAnchorRef = useRef<HTMLDivElement>(null);

  const q = query.trim().toLowerCase();

  // Build the master section list (order-stable) BEFORE applying the
  // category / query filters, so the category chip's options stay in a
  // consistent order regardless of how the search narrows the catalog.
  const allSections = useMemo(() => {
    const visible = (definitionsData ?? []).filter((d) => !d.deprecated);
    const order: string[] = [];
    const buckets = new Map<string, NodeDefinitionDto[]>();
    visible.forEach((def) => {
      const group = def.displayMetadata.group || 'Other';
      if (!buckets.has(group)) {
        buckets.set(group, []);
        order.push(group);
      }
      buckets.get(group)!.push(def);
    });
    return order.map((group) => ({ group, items: buckets.get(group)! }));
  }, [definitionsData]);

  // The categories the chip menu shows. `All` is always first.
  const categoryOptions = useMemo(
    () => [ALL_CATEGORY, ...allSections.map((s) => s.group)],
    [allSections],
  );

  const matchesQuery = (def: NodeDefinitionDto) => {
    if (!q) return true;
    const haystack = [
      def.displayMetadata.label,
      def.displayMetadata.description,
      def.id,
      ...(def.displayMetadata.tags ?? []),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  };

  // Apply the category + query filter to the master sections.
  const visibleSections = useMemo(() => {
    return allSections
      .filter((s) => category === ALL_CATEGORY || s.group === category)
      .map((s) => ({
        ...s,
        items: s.items.filter(matchesQuery),
      }))
      .filter((s) => s.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSections, category, q]);

  const isEmpty = !definitionsQuery.isLoading && visibleSections.length === 0;

  const paletteTypeFor = (def: NodeDefinitionDto): PaletteType => {
    switch (def.kind) {
      case 'input':
      case 'output':
      case 'gate':
      case 'note':
      case 'image_gen':
      case '3d_gen':
      case 'manual':
        return def.kind;
      case 'task':
      default:
        return def.id === 'task' ? 'task:_' : `task:${def.id}`;
    }
  };

  return (
    <SidebarRoot data-testid={dataTestId} aria-label="Node library">
      <FlyoutHeader>
        <FlyoutTitle>Node Library</FlyoutTitle>
        <IconButton
          ariaLabel="Close node library"
          icon={<MdClose />}
          variant="ghost"
          onClick={onClose}
          dataTestId={`${dataTestId}__close`}
        />
      </FlyoutHeader>

      <SearchRow>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search"
          aria-label="Search node library"
          stretch
          dataTestId={`${dataTestId}__search`}
        />
      </SearchRow>

      <CategoryRow>
        <CategoryAnchor ref={categoryAnchorRef}>
          <CategoryChipTrigger
            type="button"
            $isOpen={categoryMenuOpen}
            onClick={() => setCategoryMenuOpen((p) => !p)}
            aria-haspopup="menu"
            aria-expanded={categoryMenuOpen}
            title={`Category: ${category}`}
            data-testid={`${dataTestId}__category`}
          >
            <MdFilterList aria-hidden="true" />
            <CategoryChipLabel>{category}</CategoryChipLabel>
          </CategoryChipTrigger>
          {categoryMenuOpen && (
            <CategoryMenuPosition>
              <ChipMenu
                onClose={() => setCategoryMenuOpen(false)}
                ariaLabel="Filter by category"
                dataTestId={`${dataTestId}__category-menu`}
              >
                <ChipMenu.List>
                  {categoryOptions.map((opt) => (
                    <ChipMenu.Item
                      key={opt}
                      isActive={opt === category}
                      onClick={() => {
                        setCategory(opt);
                        setCategoryMenuOpen(false);
                      }}
                      dataTestId={`${dataTestId}__category-option-${opt
                        .replaceAll(/\W+/g, '-')
                        .toLowerCase()}`}
                    >
                      {opt}
                    </ChipMenu.Item>
                  ))}
                </ChipMenu.List>
              </ChipMenu>
            </CategoryMenuPosition>
          )}
        </CategoryAnchor>
      </CategoryRow>

      <SectionList>
        {definitionsQuery.isLoading && (
          <EmptyState
            size={EMPTY_STATE_SIZE_SM}
            title="Loading node catalog..."
            dataTestId={`${dataTestId}__loading`}
          />
        )}

        {definitionsQuery.isError && (
          <EmptyState
            size={EMPTY_STATE_SIZE_SM}
            title="Failed to load node catalog."
            dataTestId={`${dataTestId}__error`}
          />
        )}

        {isEmpty && !definitionsQuery.isError && (
          <EmptyState
            size={EMPTY_STATE_SIZE_SM}
            title={<>No nodes match &ldquo;{query}&rdquo;.</>}
            dataTestId={`${dataTestId}__empty`}
          />
        )}

        {visibleSections.map(({ group, items }) => (
          <Section
            key={group}
            data-testid={`${dataTestId}__section-${group.replaceAll(/\W+/g, '-').toLowerCase()}`}
          >
            <SectionHead>
              <SectionDot aria-hidden="true" />
              <SectionTitle>{group}</SectionTitle>
              <SectionRule aria-hidden="true" />
            </SectionHead>
            <ItemList>
              {items.map((def) => (
                <NodeLibraryItem
                  key={def.id}
                  paletteType={paletteTypeFor(def)}
                  label={def.displayMetadata.label}
                  description={def.displayMetadata.description}
                  icon={resolveIcon(def.displayMetadata.iconId)}
                  onSelect={onSelectPaletteType}
                />
              ))}
            </ItemList>
          </Section>
        ))}
      </SectionList>
    </SidebarRoot>
  );
};

export default NodeLibrary;

export { ItemLabel, ItemRow } from './NodeLibrary.styled';
